import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LoadingSpinner, EmptyState } from '../../components/layout/SharedComponents';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  ChevronsDown, FileText, Search, Upload, Save, X, Package,
  FileSpreadsheet, Download, Check, Lock, AlertTriangle, XCircle,
  Paperclip, ListOrdered, Eye, Receipt
} from 'lucide-react';

export function POInvoicesPanel({ orderNumber, currency, fmtTz }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subView, setSubView] = useState('recording');
  const [showLogInvoice, setShowLogInvoice] = useState(false);
  const [showOCR, setShowOCR] = useState(false);
  const [showShipping, setShowShipping] = useState(false);
  const [logMenuOpen, setLogMenuOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const navigate = useNavigate();

  const [logForm, setLogForm] = useState({ invoice_number: '', invoice_date: '', due_date: '', log_payments: false, attachment: null, lines: [] });
  const [shippingTotal, setShippingTotal] = useState(0);
  const [shippingLines, setShippingLines] = useState([]);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrFile, setOcrFile] = useState(null);
  const [poSearchQuery, setPoSearchQuery] = useState('');
  const [poSearchResults, setPoSearchResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [matchStatus, setMatchStatus] = useState(null);
  const [showFullyMatchedWarning, setShowFullyMatchedWarning] = useState(false);
  const [fullyMatchedAction, setFullyMatchedAction] = useState(null);
  const [showPoAdjustPrompt, setShowPoAdjustPrompt] = useState(false);
  const [lastCreatedInvoiceRef, setLastCreatedInvoiceRef] = useState(null);

  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importParsed, setImportParsed] = useState(null);
  const [importMappings, setImportMappings] = useState({});
  const [importStep, setImportStep] = useState('upload');
  const [importResult, setImportResult] = useState(null);
  const [importProcessing, setImportProcessing] = useState(false);

  const [poInvSort, handlePoInvSort] = useSortState('', 'asc');

  const loadData = React.useCallback(async () => {
    try {
      const [invRes, orderRes, matchRes] = await Promise.all([
        api.get(`/api/invoices/?search=${orderNumber}&page_size=50`),
        api.get(`/api/orders/${orderNumber}`),
        api.get(`/api/invoices/po-match-status/${orderNumber}`).catch(() => ({ data: null })),
      ]);
      setInvoices((invRes.data.data || []).filter(inv => inv.order_number === orderNumber));
      setOrder(orderRes.data);
      if (matchRes.data) setMatchStatus(matchRes.data);
    } catch {}
    setLoading(false);
  }, [orderNumber]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmtCurr = (v) => `${currency} ${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const isFullyMatchedAndLocked = matchStatus?.fully_matched && !matchStatus?.allow_additional_invoices;

  const tryOpenLogInvoice = () => {
    if (matchStatus?.fully_matched && matchStatus?.allow_additional_invoices) {
      setFullyMatchedAction('log');
      setShowFullyMatchedWarning(true);
      setLogMenuOpen(false);
      return;
    }
    openLogInvoice();
  };

  const tryOpenOCR = () => {
    if (matchStatus?.fully_matched && matchStatus?.allow_additional_invoices) {
      setFullyMatchedAction('ocr');
      setShowFullyMatchedWarning(true);
      setLogMenuOpen(false);
      return;
    }
    setShowOCR(true);
    setLogMenuOpen(false);
  };

  const tryOpenImport = () => {
    if (matchStatus?.fully_matched && matchStatus?.allow_additional_invoices) {
      setFullyMatchedAction('import');
      setShowFullyMatchedWarning(true);
      setLogMenuOpen(false);
      return;
    }
    openImport();
  };

  const openImport = () => {
    setShowImport(true);
    setImportFile(null);
    setImportParsed(null);
    setImportMappings({});
    setImportStep('upload');
    setImportResult(null);
    setLogMenuOpen(false);
  };

  const confirmFullyMatchedWarning = () => {
    setShowFullyMatchedWarning(false);
    if (fullyMatchedAction === 'log') openLogInvoice();
    else if (fullyMatchedAction === 'ocr') setShowOCR(true);
    else if (fullyMatchedAction === 'import') openImport();
    setFullyMatchedAction(null);
  };

  const openLogInvoice = () => {
    const today = new Date().toISOString().split('T')[0];
    const lines = (order?.line_items || []).map(li => ({
      item_num: li.item_num, item_description: li.item_description, item_code: li.item_code,
      gross_price: li.net_price || 0, invoice_amount: li.net_price || 0,
      selected: true, id: li.id,
    }));
    setLogForm({ invoice_number: '', invoice_date: today, due_date: today, log_payments: false, attachment: null, lines });
    setShowLogInvoice(true);
    setLogMenuOpen(false);
  };

  const openShipping = () => {
    const lines = (order?.line_items || []).map(li => ({
      item_num: li.item_num, item_description: li.item_description,
      shipping: li.shipping_charges || 0, id: li.id,
    }));
    setShippingLines(lines);
    setShippingTotal(lines.reduce((s, l) => s + (l.shipping || 0), 0));
    setShowShipping(true);
    setLogMenuOpen(false);
  };

  const adjustShipping = () => {
    const perItem = shippingLines.length > 0 ? Math.round(shippingTotal / shippingLines.length * 100) / 100 : 0;
    setShippingLines(prev => prev.map(l => ({ ...l, shipping: perItem })));
  };

  const submitLogInvoice = async () => {
    if (!logForm.invoice_number.trim()) { toast.error('Invoice number is required'); return; }
    const selectedLines = logForm.lines.filter(l => l.selected);
    if (selectedLines.length === 0) { toast.error('Select at least one line item'); return; }
    setSubmitting(true);
    try {
      const lineItems = selectedLines.map(l => ({
        po_item_id: l.id, item_num: l.item_num, item_code: l.item_code || '',
        item_description: l.item_description, invoice_quantity: 1,
        invoice_unit_price: l.invoice_amount, invoice_net_price: l.invoice_amount, unit: 'EACH',
      }));
      const r = await api.post('/api/invoices/', {
        supplier_invoice_number: logForm.invoice_number,
        supplier_code: order?.supplier_code || '', supplier_name: order?.supplier_name || '',
        order_number: orderNumber, invoice_date: logForm.invoice_date, due_date: logForm.due_date,
        line_items: lineItems,
      });
      toast.success(r.data.message);
      try { await api.post(`/api/invoices/${r.data.invoice_ref}/match`); } catch {}
      setShowLogInvoice(false);

      const invoiceTotal = selectedLines.reduce((s, l) => s + l.invoice_amount, 0);
      const poTotal = order?.net_total || 0;
      const existingInvoiced = matchStatus?.total_invoiced || 0;
      if ((existingInvoiced + invoiceTotal) > poTotal && poTotal > 0) {
        setLastCreatedInvoiceRef(r.data.invoice_ref);
        setShowPoAdjustPrompt(true);
      }
      loadData();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to log invoice')); }
    setSubmitting(false);
  };

  const saveShipping = async () => {
    try {
      const lineItems = [...(order?.line_items || [])];
      for (const sl of shippingLines) {
        const li = lineItems.find(l => l.id === sl.id);
        if (li) li.shipping_charges = sl.shipping;
      }
      await api.put(`/api/orders/${orderNumber}/header`, { line_items: lineItems });
      toast.success('Shipping charges saved');
      setShowShipping(false);
      loadData();
    } catch (e) { toast.error('Save failed'); }
  };

  const processOCR = async (file) => {
    if (!file) return;
    setOcrProcessing(true);
    setOcrResult(null);
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
      const r = await api.post('/api/invoices/ocr/process', { file_data: fileData, file_name: file.name, order_number: orderNumber });
      setOcrResult(r.data);
      if (r.data.warnings?.length > 0) {
        r.data.warnings.forEach(w => {
          if (w.severity === 'error') toast.error(w.message);
          else toast.warning(w.message);
        });
      }
      if (!r.data.po_found && !r.data.suggested_pos?.length) {
        toast.warning('No matching purchase order found.');
      } else if (r.data.po_found) {
        toast.success(`Matched PO ${r.data.po_data.order_number}`);
      } else {
        toast.info(`${r.data.suggested_pos.length} possible PO match(es) found.`);
      }
    } catch (e) { toast.error(getApiErrorMessage(e, 'OCR processing failed')); }
    setOcrProcessing(false);
  };

  const createFromOCR = async (useOrderNumber, createGhost = false) => {
    if (!ocrResult?.extracted) return;
    const overrideWarnings = (ocrResult.warnings || []).filter(w => w.requires_override);
    if (overrideWarnings.length > 0) {
      const msg = overrideWarnings.map(w => w.message).join('\n\n') + '\n\nDo you want to override and proceed?';
      if (!window.confirm(msg)) return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/api/invoices/ocr/create', {
        extracted: ocrResult.extracted, order_number: useOrderNumber || '', create_ghost_po: createGhost,
        overrides: overrideWarnings.map(w => w.type),
      });
      toast.success(r.data.message);
      if (r.data.invoice_ref) {
        try { await api.post(`/api/invoices/${r.data.invoice_ref}/match`); } catch {}
      }

      const invoiceTotal = ocrResult.extracted.total || ocrResult.extracted.subtotal || 0;
      const poTotal = order?.net_total || 0;
      const existingInvoiced = matchStatus?.total_invoiced || 0;
      if (useOrderNumber && (existingInvoiced + invoiceTotal) > poTotal && poTotal > 0) {
        setLastCreatedInvoiceRef(r.data.invoice_ref);
        setShowPoAdjustPrompt(true);
      }

      setShowOCR(false); setOcrResult(null); setOcrFile(null);
      loadData();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create invoice')); }
    setSubmitting(false);
  };

  const searchPOs = async () => {
    if (!poSearchQuery.trim()) return;
    try {
      const r = await api.get(`/api/orders/?search=${encodeURIComponent(poSearchQuery)}&page_size=10`);
      setPoSearchResults(r.data.data || []);
    } catch { setPoSearchResults([]); }
  };

  const adjustPoFromInvoice = async () => {
    if (!lastCreatedInvoiceRef) return;
    setSubmitting(true);
    try {
      const r = await api.post('/api/invoices/adjust-po-from-invoice', {
        order_number: orderNumber,
        invoice_ref: lastCreatedInvoiceRef,
      });
      toast.success(r.data.message);
      setShowPoAdjustPrompt(false);
      setLastCreatedInvoiceRef(null);
      loadData();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to adjust PO')); }
    setSubmitting(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/api/invoices/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'invoice_import_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const parseImportFile = async (file) => {
    if (!file) return;
    setImportFile(file);
    setImportProcessing(true);
    try {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
      const r = await api.post('/api/invoices/import/parse', { file_data: fileData, file_name: file.name });
      setImportParsed(r.data);
      setImportMappings(r.data.suggested_mappings || {});
      setImportStep('map');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to parse file')); }
    setImportProcessing(false);
  };

  const runImport = async () => {
    if (!importParsed || !importFile) return;
    setImportProcessing(true);
    try {
      const r = await api.post('/api/invoices/import/create', {
        rows: importParsed.rows || importParsed.preview,
        mappings: importMappings,
        default_order_number: orderNumber,
      });
      setImportResult(r.data);
      setImportStep('result');
      if (r.data.created_count > 0) toast.success(`${r.data.created_count} invoice(s) imported`);
      if (r.data.error_count > 0) toast.warning(`${r.data.error_count} error(s)`);
      loadData();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Import failed')); }
    setImportProcessing(false);
  };

  const IMPORT_FIELDS = [
    { key: 'supplier_invoice_number', label: 'Supplier Invoice Number', required: true },
    { key: 'supplier_code', label: 'Supplier Code', required: true },
    { key: 'supplier_name', label: 'Supplier Name' },
    { key: 'invoice_date', label: 'Invoice Date', required: true },
    { key: 'due_date', label: 'Due Date' },
    { key: 'item_description', label: 'Item Description', required: true },
    { key: 'quantity', label: 'Quantity', required: true },
    { key: 'unit_price', label: 'Unit Price', required: true },
    { key: 'net_price', label: 'Net Price' },
    { key: 'unit', label: 'Unit' },
    { key: 'tax', label: 'Tax' },
    { key: 'notes', label: 'Notes' },
  ];

  const totalInvoiced = invoices.reduce((s, i) => s + (i.invoice_gross_total || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + (i.amount_paid || 0), 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Sub-view tabs */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-primary">Invoices</h3>
        <div className="flex items-center gap-1 text-xs">
          {[
            { id: 'recording', label: 'Invoice Recording/Review' },
            { id: 'attach', label: 'Attach Invoices' },
            { id: 'history', label: 'History & Matching' },
          ].map(t => (
            <button key={t.id} onClick={() => setSubView(t.id)}
              className={`px-3 py-1 rounded ${subView === t.id ? 'text-primary font-semibold underline' : 'text-muted-foreground hover:text-primary'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice Recording/Review */}
      {subView === 'recording' && (
        <div>
          <div className="relative inline-block mb-3">
            <button onClick={() => setLogMenuOpen(!logMenuOpen)} className="flex items-center gap-1.5 font-bold text-primary hover:opacity-80">
              <ChevronsDown className="h-5 w-5 text-orange-500" strokeWidth={3} /> Invoice Recording/Review
            </button>
            {logMenuOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-card border rounded-lg shadow-xl py-1 w-52">
                {isFullyMatchedAndLocked ? (
                  <>
                    <div className="px-4 py-2 text-xs text-muted-foreground italic" data-testid="invoice-locked-msg">
                      <Lock className="h-3 w-3 inline mr-1" />Invoice logging is locked — this PO is fully matched.
                    </div>
                    <button className="w-full text-left px-4 py-2 text-sm text-muted-foreground cursor-not-allowed flex items-center gap-2 opacity-50" disabled data-testid="log-invoice-btn-disabled">
                      <FileText className="h-3.5 w-3.5" /> Log Invoice
                    </button>
                    <button className="w-full text-left px-4 py-2 text-sm text-muted-foreground cursor-not-allowed flex items-center gap-2 opacity-50" disabled data-testid="log-invoice-ocr-btn-disabled">
                      <Search className="h-3.5 w-3.5" /> Log Invoice with OCR
                    </button>
                    <button className="w-full text-left px-4 py-2 text-sm text-muted-foreground cursor-not-allowed flex items-center gap-2 opacity-50" disabled data-testid="import-invoice-btn-disabled">
                      <FileSpreadsheet className="h-3.5 w-3.5" /> Import from Spreadsheet
                    </button>
                  </>
                ) : (
                  <>
                    <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={tryOpenLogInvoice} data-testid="log-invoice-btn">
                      <FileText className="h-3.5 w-3.5" /> Log Invoice
                    </button>
                    <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={tryOpenOCR} data-testid="log-invoice-ocr-btn">
                      <Search className="h-3.5 w-3.5" /> Log Invoice with OCR
                    </button>
                    <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={tryOpenImport} data-testid="import-invoice-btn">
                      <FileSpreadsheet className="h-3.5 w-3.5" /> Import from Spreadsheet
                    </button>
                  </>
                )}
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-accent flex items-center gap-2" onClick={openShipping} data-testid="shipping-charges-btn">
                  <Package className="h-3.5 w-3.5" /> Shipping Charges
                </button>
              </div>
            )}
          </div>

          {matchStatus?.fully_matched && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-xs" data-testid="fully-matched-banner">
              <Check className="h-4 w-4 text-green-600" />
              <span className="text-green-700 font-semibold">This PO is fully matched.</span>
              <span className="text-green-600">
                Total invoiced: {fmtCurr(matchStatus.total_invoiced)} across {matchStatus.invoice_count} invoice(s) against PO total of {fmtCurr(matchStatus.po_total)}.
              </span>
              {!matchStatus.allow_additional_invoices && (
                <span className="text-red-600 font-semibold ml-1"><Lock className="h-3 w-3 inline" /> Additional invoices locked.</span>
              )}
            </div>
          )}

          <table className="w-full text-[11px] border-collapse">
            <thead><tr className="bg-muted/50 border-b">
              <SortableTableHeader label="Item" field="item_num" sortState={poInvSort} onSort={handlePoInvSort} />
              <SortableTableHeader label="Qty" field="quantity" sortState={poInvSort} onSort={handlePoInvSort} />
              <SortableTableHeader label="Description" field="item_description" sortState={poInvSort} onSort={handlePoInvSort} />
              <SortableTableHeader label="Discount" field="discount" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Sales Tax" field="sales_tax" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Unit Price" field="unit_price" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Net Price" field="net_price" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Shipping" field="shipping_charges" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label={`Gross Price (${currency})`} field="_gross" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Invoice Amt" field="_inv_amt" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Amt Paid" field="_paid" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
              <SortableTableHeader label="Balance" field="_balance" sortState={poInvSort} onSort={handlePoInvSort} className="text-right" />
            </tr></thead>
            <tbody>
              {(order?.line_items || []).map(li => {
                const gross = (li.net_price || 0) + (li.shipping_charges || 0);
                const invAmt = invoices.reduce((s, inv) => s + (inv.line_items || []).filter(il => il.po_item_id === li.id).reduce((ls, il) => ls + (il.invoice_net_price || 0), 0), 0);
                const paid = 0;
                const balance = gross - invAmt;
                return (
                  <tr key={li.id} className="border-b hover:bg-accent/20">
                    <td className="px-2 py-1.5">{li.item_num}</td>
                    <td className="px-2 py-1.5">{li.quantity}</td>
                    <td className="px-2 py-1.5 max-w-[120px] truncate">{li.item_description}</td>
                    <td className="px-2 py-1.5 text-right">{(li.discount || 0).toFixed(2)}%</td>
                    <td className="px-2 py-1.5 text-right">{(li.sales_tax || 0).toFixed(3)}%</td>
                    <td className="px-2 py-1.5 text-right">{fmtNum(li.unit_price)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtNum(li.net_price)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtNum(li.shipping_charges || 0)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmtNum(gross)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtNum(invAmt)}</td>
                    <td className="px-2 py-1.5 text-right text-green-700">{fmtNum(paid)}</td>
                    <td className={`px-2 py-1.5 text-right font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-700'}`}>{fmtNum(balance)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="border-t-2 font-semibold text-[11px]">
              <td colSpan={5}></td>
              <td className="px-2 py-2 text-right">Total</td>
              <td className="px-2 py-2 text-right">{fmtNum((order?.line_items || []).reduce((s, l) => s + (l.net_price || 0), 0))}</td>
              <td className="px-2 py-2 text-right">{fmtNum((order?.line_items || []).reduce((s, l) => s + (l.shipping_charges || 0), 0))}</td>
              <td className="px-2 py-2 text-right">{fmtNum((order?.line_items || []).reduce((s, l) => s + (l.net_price || 0) + (l.shipping_charges || 0), 0))}</td>
              <td className="px-2 py-2 text-right">{fmtNum(totalInvoiced)}</td>
              <td className="px-2 py-2 text-right text-green-700">{fmtNum(totalPaid)}</td>
              <td className="px-2 py-2 text-right font-semibold">{fmtNum((order?.line_items || []).reduce((s, l) => s + (l.net_price || 0) + (l.shipping_charges || 0), 0) - totalInvoiced)}</td>
            </tr></tfoot>
          </table>
        </div>
      )}

      {/* Attach Invoices */}
      {subView === 'attach' && (
        <div>
          <h4 className="text-sm font-semibold mb-3">Read PDF Invoice — Order {orderNumber}</h4>
          <div className="border-2 border-dashed rounded-xl p-8 text-center"
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
            onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); const f = e.dataTransfer.files[0]; setOcrFile(f); processOCR(f); setShowOCR(true); }}>
            <p className="text-muted-foreground mb-3">Drag & drop invoice file here, or click Select File.</p>
            <label>
              <Button size="sm" onClick={() => {}}><Upload className="h-3.5 w-3.5 mr-1" /> Select File</Button>
              <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => { const f = e.target.files?.[0]; if (f) { setOcrFile(f); processOCR(f); setShowOCR(true); } }} />
            </label>
          </div>
        </div>
      )}

      {/* History & Matching */}
      {subView === 'history' && (
        <div>
          <h4 className="text-sm font-semibold mb-3">Invoice History & Matching</h4>
          {invoices.length === 0 ? (
            <EmptyState title="No invoices" description="No invoices have been raised against this purchase order." />
          ) : (
            <div className="space-y-3">
              {invoices.map(inv => {
                const balance = (inv.invoice_gross_total || 0) - (inv.amount_paid || 0);
                return (
                  <div key={inv.invoice_ref} className="border rounded-lg p-3 hover:bg-accent/20 cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_ref}`)}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary font-bold">{inv.invoice_ref}</span>
                        <span className="text-xs text-muted-foreground">#{inv.supplier_invoice_number}</span>
                        <Badge className={`text-[9px] ${inv.match_status === 'MATCHED' ? 'bg-green-100 text-green-700' : inv.match_status === 'VARIANCE' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{inv.match_status || 'PENDING'}</Badge>
                        <Badge className={`text-[9px] ${inv.status === 'PAID' ? 'bg-green-200 text-green-800' : inv.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>{(inv.status || '').replace(/_/g, ' ')}</Badge>
                      </div>
                      <span className={`text-sm font-mono font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-700'}`}>{fmtCurr(balance)} due</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Date: {inv.invoice_date} | Total: {fmtCurr(inv.invoice_gross_total)} | Paid: {fmtCurr(inv.amount_paid)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Log Invoice Dialog */}
      {showLogInvoice && (
        <Dialog open onOpenChange={() => setShowLogInvoice(false)}>
          <DialogContent className="max-w-[90vw] lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Log Invoice For All Outstanding Items — Order {orderNumber}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground mb-3">For each unpaid item on the order, this function sets the invoice and payment amount to equal the net value of the item.</p>

            <div className="grid grid-cols-[1fr_auto_auto] gap-3 mb-4 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-red-500 font-semibold">Invoice Number</Label>
                <Input value={logForm.invoice_number} onChange={e => setLogForm(f => ({ ...f, invoice_number: e.target.value }))} className="h-8 text-sm" />
              </div>
              <label className="flex items-center gap-2 h-8 cursor-pointer">
                <input type="checkbox" checked={logForm.log_payments} onChange={e => setLogForm(f => ({ ...f, log_payments: e.target.checked }))} className="h-4 w-4 rounded" />
                <span className="text-xs">Log Payments</span>
              </label>
              <p className="text-sm font-semibold">Total Invoice: <span className="text-primary">{fmtNum(logForm.lines.filter(l => l.selected).reduce((s, l) => s + l.invoice_amount, 0))}</span></p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="space-y-1">
                <Label className="text-xs text-red-500 font-semibold">Invoice Date</Label>
                <Input type="date" value={logForm.invoice_date} onChange={e => setLogForm(f => ({ ...f, invoice_date: e.target.value }))} className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-red-500 font-semibold">Payment Due Date</Label>
                <Input type="date" value={logForm.due_date} onChange={e => setLogForm(f => ({ ...f, due_date: e.target.value }))} className="h-8 text-sm" />
              </div>
            </div>

            <div className="space-y-1 mb-4">
              <Label className="text-xs text-primary font-semibold">Select Invoice Attachment</Label>
              <div className="flex items-center gap-2">
                <label className="flex-1 h-8 flex items-center rounded-lg border border-input bg-background px-3 cursor-pointer text-xs text-muted-foreground hover:bg-accent">
                  {logForm.attachment ? logForm.attachment.name : '--Select--'}
                  <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => setLogForm(f => ({ ...f, attachment: e.target.files?.[0] || null }))} />
                </label>
                <Paperclip className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            <table className="w-full text-xs border-collapse border">
              <thead><tr className="bg-muted/50 border-b">
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2">Gross Price</th>
                <th className="text-right px-3 py-2">Invoice Amount</th>
                <th className="text-center px-3 py-2">
                  <input type="checkbox" checked={logForm.lines.every(l => l.selected)} onChange={e => setLogForm(f => ({ ...f, lines: f.lines.map(l => ({ ...l, selected: e.target.checked })) }))} className="h-4 w-4" />
                </th>
              </tr></thead>
              <tbody>
                {logForm.lines.map((l, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-3 py-1.5">{l.item_num}</td>
                    <td className="px-3 py-1.5">{l.item_description}</td>
                    <td className="px-3 py-1.5 text-right text-primary font-mono">{fmtNum(l.gross_price)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input type="number" step="0.01" value={l.invoice_amount} onChange={e => {
                        const newLines = [...logForm.lines];
                        newLines[i] = { ...newLines[i], invoice_amount: parseFloat(e.target.value) || 0 };
                        setLogForm(f => ({ ...f, lines: newLines }));
                      }} className="h-7 w-28 text-xs text-right" />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <input type="checkbox" checked={l.selected} onChange={e => {
                        const newLines = [...logForm.lines];
                        newLines[i] = { ...newLines[i], selected: e.target.checked };
                        setLogForm(f => ({ ...f, lines: newLines }));
                      }} className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t-2 font-semibold">
                <td colSpan={2}></td>
                <td className="px-3 py-2 text-right font-mono">{fmtNum(logForm.lines.reduce((s, l) => s + l.gross_price, 0))}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtNum(logForm.lines.filter(l => l.selected).reduce((s, l) => s + l.invoice_amount, 0))}</td>
                <td></td>
              </tr></tfoot>
            </table>

            <div className="flex justify-start gap-2 mt-4">
              <Button onClick={submitLogInvoice} disabled={submitting}><Save className="h-3.5 w-3.5 mr-1" /> {submitting ? 'Logging...' : 'Log Invoice'}</Button>
              <Button variant="outline" onClick={() => setShowLogInvoice(false)}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Shipping Charges Dialog */}
      {showShipping && (
        <Dialog open onOpenChange={() => setShowShipping(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Shipping Charges — Order {orderNumber}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-3 mb-3">
              <div className="space-y-1 flex-1">
                <Label className="text-xs font-semibold">Enter Total Shipping Charges</Label>
                <Input type="number" step="0.01" value={shippingTotal} onChange={e => setShippingTotal(parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
              </div>
              <Button size="sm" onClick={adjustShipping} className="mt-5"><ListOrdered className="h-3.5 w-3.5 mr-1" /> Adjust</Button>
            </div>
            <p className="text-[10px] text-muted-foreground mb-3">Note: Click 'Adjust' button to split the shipping charges equally among the items listed below and then click 'Save' button.</p>
            <table className="w-full text-xs border-collapse border">
              <thead><tr className="bg-muted/50 border-b">
                <th className="text-left px-3 py-2">Item Order</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2">Shipping Charges Per Item</th>
              </tr></thead>
              <tbody>
                {shippingLines.map((l, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-3 py-1.5">{l.item_num}</td>
                    <td className="px-3 py-1.5">{l.item_description}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input type="number" step="0.01" value={l.shipping} onChange={e => {
                        const newLines = [...shippingLines];
                        newLines[i] = { ...newLines[i], shipping: parseFloat(e.target.value) || 0 };
                        setShippingLines(newLines);
                      }} className="h-7 w-28 text-xs text-right" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-start gap-2 mt-4">
              <Button onClick={saveShipping}><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
              <Button variant="outline" onClick={() => setShowShipping(false)}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* OCR Dialog */}
      {showOCR && (
        <Dialog open onOpenChange={() => { setShowOCR(false); setOcrResult(null); setOcrFile(null); }}>
          <DialogContent className="max-w-[95vw] xl:max-w-5xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-5 pt-5 pb-3">
              <DialogTitle>{ocrResult ? 'OCR Results — Review & Confirm' : `Read PDF Invoice — Order ${orderNumber}`}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {!ocrResult && (
                <div>
                  <div className="border-2 border-dashed rounded-xl p-10 text-center mb-4"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                    onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); const f = e.dataTransfer.files[0]; setOcrFile(f); }}>
                    <p className="text-muted-foreground mb-3">{ocrFile ? ocrFile.name : 'Drag & drop invoice file here.'}</p>
                    <label>
                      <Button size="sm" variant={ocrFile ? 'outline' : 'default'} onClick={() => {}}><Upload className="h-3.5 w-3.5 mr-1" /> Select File</Button>
                      <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => setOcrFile(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  <div className="flex justify-start gap-2">
                    {ocrFile && <Button onClick={() => processOCR(ocrFile)} disabled={ocrProcessing}>{ocrProcessing ? 'Processing with AI...' : 'Process Invoice'}</Button>}
                    <Button variant="outline" onClick={() => { setShowOCR(false); setOcrFile(null); }}>Cancel</Button>
                  </div>
                </div>
              )}

              {ocrResult && (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="rounded-lg p-4" style={{ background: '#eff6ff' }}>
                      <h4 className="text-xs font-bold text-blue-800 uppercase mb-3">Extracted Invoice Data</h4>
                      <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                        <div><p className="text-[10px] text-blue-600">Invoice #</p><p className="font-semibold">{ocrResult.extracted.invoice_number || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">Supplier</p><p className="font-semibold">{ocrResult.extracted.supplier_name || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">Invoice Total</p><p className="font-semibold">{ocrResult.extracted.currency || 'USD'} {fmtNum(ocrResult.extracted.total)}</p></div>
                        <div><p className="text-[10px] text-blue-600">Invoice Date</p><p>{ocrResult.extracted.invoice_date || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">Due Date</p><p>{ocrResult.extracted.due_date || '—'}</p></div>
                        <div><p className="text-[10px] text-blue-600">PO Reference</p><p>{ocrResult.extracted.purchase_order_number || 'None'}</p></div>
                      </div>
                      <table className="w-full text-[11px] border-collapse bg-white/70 rounded">
                        <thead><tr className="border-b bg-blue-100/50"><th className="text-left px-2 py-1.5 w-8">#</th><th className="text-left px-2 py-1.5">Description</th><th className="text-right px-2 py-1.5 w-14">Qty</th><th className="text-right px-2 py-1.5 w-20">Price</th><th className="text-right px-2 py-1.5 w-24">Net</th></tr></thead>
                        <tbody>
                          {(ocrResult.extracted.line_items || []).map((li, i) => (
                            <tr key={i} className="border-b"><td className="px-2 py-1">{i+1}</td><td className="px-2 py-1">{li.item_description}</td><td className="px-2 py-1 text-right">{li.quantity}</td><td className="px-2 py-1 text-right">{fmtNum(li.unit_price)}</td><td className="px-2 py-1 text-right font-semibold">{fmtNum(li.net_price)}</td></tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t"><td colSpan={4} className="px-2 py-1 text-right text-[10px] text-muted-foreground">Subtotal</td><td className="px-2 py-1 text-right">{fmtNum(ocrResult.extracted.subtotal)}</td></tr>
                          <tr><td colSpan={4} className="px-2 py-0.5 text-right text-[10px] text-muted-foreground">Tax</td><td className="px-2 py-0.5 text-right">{fmtNum(ocrResult.extracted.tax)}</td></tr>
                          <tr><td colSpan={4} className="px-2 py-0.5 text-right text-[10px] text-muted-foreground">Shipping</td><td className="px-2 py-0.5 text-right">{fmtNum(ocrResult.extracted.shipping)}</td></tr>
                          <tr className="border-t-2 font-bold"><td colSpan={4} className="px-2 py-1.5 text-right">Total ({ocrResult.extracted.currency || 'USD'})</td><td className="px-2 py-1.5 text-right text-blue-700">{fmtNum(ocrResult.extracted.total)}</td></tr>
                        </tfoot>
                      </table>
                    </div>

                    <div className="rounded-lg p-4" style={{ background: ocrResult.po_found ? '#f0fdf4' : '#fffbeb' }}>
                      <h4 className={`text-xs font-bold uppercase mb-3 ${ocrResult.po_found ? 'text-green-800' : 'text-amber-800'}`}>Purchase Order</h4>
                      {ocrResult.po_found ? (
                        <div>
                          <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                            <div><p className="text-[10px] text-green-600">PO Number</p><p className="font-semibold">{ocrResult.po_data.order_number}</p></div>
                            <div><p className="text-[10px] text-green-600">Supplier</p><p className="font-semibold">{ocrResult.po_data.supplier_name}</p></div>
                            <div><p className="text-[10px] text-green-600">PO Total</p><p className="font-semibold">{currency} {fmtNum(ocrResult.po_data.net_total)}</p></div>
                            <div><p className="text-[10px] text-green-600">Status</p><Badge className="text-[9px]">{ocrResult.po_data.status}</Badge></div>
                            <div><p className="text-[10px] text-green-600">Currency</p><p className="font-semibold">{currency}</p></div>
                            <div></div>
                          </div>
                          <table className="w-full text-[11px] border-collapse bg-white/70 rounded">
                            <thead><tr className="border-b bg-green-100/50"><th className="text-left px-2 py-1.5 w-8">#</th><th className="text-left px-2 py-1.5">Description</th><th className="text-right px-2 py-1.5 w-14">Qty</th><th className="text-right px-2 py-1.5 w-20">Price</th><th className="text-right px-2 py-1.5 w-24">Net</th></tr></thead>
                            <tbody>
                              {(ocrResult.po_data.line_items || []).map((li, i) => {
                                const invLine = (ocrResult.extracted.line_items || [])[i];
                                const priceDiff = invLine && Math.abs((invLine.unit_price || 0) - (li.unit_price || 0)) > 0.01;
                                const qtyDiff = invLine && Math.abs((invLine.quantity || 0) - (li.quantity || 0)) > 0.01;
                                const netDiff = invLine && Math.abs((invLine.net_price || 0) - (li.net_price || 0)) > 0.01;
                                return (
                                  <tr key={i} className="border-b">
                                    <td className="px-2 py-1">{li.item_num || i+1}</td>
                                    <td className="px-2 py-1">{li.item_description}</td>
                                    <td className={`px-2 py-1 text-right ${qtyDiff ? 'text-red-600 font-bold bg-red-50' : ''}`}>{li.quantity}</td>
                                    <td className={`px-2 py-1 text-right ${priceDiff ? 'text-red-600 font-bold bg-red-50' : ''}`}>{fmtNum(li.unit_price)}</td>
                                    <td className={`px-2 py-1 text-right font-semibold ${netDiff ? 'text-red-600 font-bold bg-red-50' : ''}`}>{fmtNum(li.net_price)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="border-t"><td colSpan={4} className="px-2 py-1 text-right text-[10px] text-muted-foreground">Subtotal</td><td className="px-2 py-1 text-right">{fmtNum(ocrResult.po_data.net_total)}</td></tr>
                              <tr><td colSpan={4} className="px-2 py-0.5 text-right text-[10px] text-muted-foreground">Tax</td><td className="px-2 py-0.5 text-right">{fmtNum(0)}</td></tr>
                              <tr className="border-t-2 font-bold"><td colSpan={4} className="px-2 py-1.5 text-right">Total ({currency})</td><td className="px-2 py-1.5 text-right text-green-700">{fmtNum(ocrResult.po_data.net_total)}</td></tr>
                            </tfoot>
                          </table>
                          {(ocrResult.warnings || []).length > 0 && (
                            <div className="mt-3 space-y-2">
                              {ocrResult.warnings.map((w, i) => (
                                <div key={i} className={`rounded-lg px-3 py-2 text-xs font-semibold ${w.severity === 'error' ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}>
                                  {w.severity === 'error' && <XCircle className="h-3.5 w-3.5 inline mr-1" />}
                                  {w.severity === 'warning' && <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                                  {w.message}
                                  {w.requires_override && <span className="ml-1 text-[10px] opacity-70">(override required)</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {(ocrResult.warnings || []).length === 0 && (
                            <p className="mt-2 text-xs text-green-700 font-semibold">All checks passed.</p>
                          )}
                          <div className="mt-3">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => createFromOCR(ocrResult.po_data.order_number)}
                              disabled={submitting || ocrResult.warnings?.some(w => w.severity === 'error')}>
                              <Check className="h-3.5 w-3.5 mr-1" /> {submitting ? 'Creating...' : ocrResult.warnings?.some(w => w.requires_override) ? 'Override & Create Supplemental Invoice Against This PO' : 'Create Invoice Against This PO'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 mb-3">
                            <p className="text-sm font-semibold text-amber-700">Purchase order not found</p>
                            <p className="text-xs text-amber-600 mt-1">The PO number from the invoice did not match any orders. You can search manually or create a ghost PO.</p>
                          </div>
                          <div className="flex gap-2 mb-3">
                            <Input value={poSearchQuery} onChange={e => setPoSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchPOs()} className="h-8 text-sm" placeholder="Search PO number or supplier..." />
                            <Button size="sm" variant="outline" onClick={searchPOs}><Search className="h-3.5 w-3.5" /></Button>
                          </div>
                          {poSearchResults.length > 0 && (
                            <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                              {poSearchResults.map(po => (
                                <button key={po.order_number} onClick={() => createFromOCR(po.order_number)}
                                  className="w-full text-left px-3 py-2 rounded border hover:bg-accent text-xs flex items-center justify-between">
                                  <span><span className="font-mono font-semibold text-primary">{po.order_number}</span> — {po.supplier_name}</span>
                                  <span className="text-muted-foreground">{fmtNum(po.net_total)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {ocrResult.suggested_pos?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-[10px] font-semibold text-muted-foreground mb-1">Suggested matches by supplier:</p>
                              {ocrResult.suggested_pos.map(po => (
                                <button key={po.order_number} onClick={() => createFromOCR(po.order_number)}
                                  className="w-full text-left px-3 py-2 rounded border hover:bg-accent text-xs flex items-center justify-between mb-1">
                                  <span><span className="font-mono font-semibold text-primary">{po.order_number}</span> — {po.supplier_name}</span>
                                  <Badge className="text-[9px]">{po.status}</Badge>
                                </button>
                              ))}
                            </div>
                          )}
                          <Button size="sm" variant="outline" onClick={() => createFromOCR('', true)} disabled={submitting}>
                            {submitting ? 'Creating...' : 'Create Invoice with Ghost PO'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-start gap-2 pt-3 border-t">
                    <Button variant="outline" onClick={() => { setOcrResult(null); setOcrFile(null); }}>Upload Different File</Button>
                    <Button variant="ghost" onClick={() => { setShowOCR(false); setOcrResult(null); setOcrFile(null); }}>Close</Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Fully Matched Warning Dialog */}
      {showFullyMatchedWarning && (
        <Dialog open onOpenChange={() => { setShowFullyMatchedWarning(false); setFullyMatchedAction(null); }}>
          <DialogContent className="max-w-md" data-testid="fully-matched-warning-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" /> Fully Matched Order
              </DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <p className="text-sm mb-3">This order is already fully matched. Are you sure you want to add another invoice?</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 space-y-1">
                <p><strong>PO Total:</strong> {fmtCurr(matchStatus?.po_total)}</p>
                <p><strong>Already Invoiced:</strong> {fmtCurr(matchStatus?.total_invoiced)} ({matchStatus?.invoice_count} invoice(s))</p>
              </div>
            </div>
            <div className="flex justify-start gap-2 pt-2">
              <Button onClick={confirmFullyMatchedWarning} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-fully-matched-btn">
                <Check className="h-3.5 w-3.5 mr-1" /> Yes, Proceed
              </Button>
              <Button variant="outline" onClick={() => { setShowFullyMatchedWarning(false); setFullyMatchedAction(null); }} data-testid="cancel-fully-matched-btn">
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* PO Adjustment Prompt Dialog */}
      {showPoAdjustPrompt && (
        <Dialog open onOpenChange={() => { setShowPoAdjustPrompt(false); setLastCreatedInvoiceRef(null); }}>
          <DialogContent className="max-w-lg" data-testid="po-adjust-prompt-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <FileText className="h-5 w-5" /> Invoice Exceeds Purchase Order Value
              </DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <p className="text-sm mb-3">The invoice value exceeds the purchase order value. Assuming this is correct, do you want to make purchase order adjustments to reflect this new invoice?</p>
              <p className="text-xs text-muted-foreground mb-3">New lines will be added to PO {orderNumber} based on the invoice line items. Existing lines will not be modified.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 space-y-1">
                <p><strong>Invoice Ref:</strong> {lastCreatedInvoiceRef}</p>
                <p><strong>Current PO Total:</strong> {fmtCurr(order?.net_total)}</p>
              </div>
            </div>
            <div className="flex justify-start gap-2 pt-2">
              <Button onClick={adjustPoFromInvoice} disabled={submitting} data-testid="confirm-po-adjust-btn">
                <Check className="h-3.5 w-3.5 mr-1" /> {submitting ? 'Adjusting...' : 'Yes, Adjust Purchase Order'}
              </Button>
              <Button variant="outline" onClick={() => { setShowPoAdjustPrompt(false); setLastCreatedInvoiceRef(null); }} data-testid="skip-po-adjust-btn">
                <X className="h-3.5 w-3.5 mr-1" /> No, Skip
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Spreadsheet Import Dialog */}
      {showImport && (
        <Dialog open onOpenChange={() => setShowImport(false)}>
          <DialogContent className="max-w-[90vw] xl:max-w-4xl max-h-[90vh] flex flex-col p-0" data-testid="po-import-invoice-dialog">
            <DialogHeader className="px-5 pt-5 pb-3">
              <DialogTitle>
                {importStep === 'upload' && `Import Invoices — Order ${orderNumber}`}
                {importStep === 'map' && 'Map Columns to Invoice Fields'}
                {importStep === 'preview' && 'Review & Import'}
                {importStep === 'result' && 'Import Results'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {importStep === 'upload' && (
                <div>
                  <p className="text-xs text-muted-foreground mb-3">Import invoices for PO {orderNumber}. The PO number will be automatically linked.</p>
                  <div className="flex gap-3 mb-4">
                    <Button size="sm" variant="outline" onClick={downloadTemplate}>
                      <Download className="h-3.5 w-3.5 mr-1" /> Download Template
                    </Button>
                  </div>
                  <div className="border-2 border-dashed rounded-xl p-8 text-center mb-4"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                    onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); parseImportFile(e.dataTransfer.files[0]); }}>
                    <p className="text-muted-foreground mb-3">{importFile ? importFile.name : 'Drag & drop Excel or CSV file here.'}</p>
                    <label>
                      <Button size="sm" variant={importFile ? 'outline' : 'default'} onClick={() => {}}><Upload className="h-3.5 w-3.5 mr-1" /> Select File</Button>
                      <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={e => { const f = e.target.files?.[0]; if (f) parseImportFile(f); }} />
                    </label>
                  </div>
                  {importProcessing && <p className="text-sm text-muted-foreground text-center">Parsing file...</p>}
                </div>
              )}

              {importStep === 'map' && importParsed && (
                <div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Found <strong>{importParsed.row_count}</strong> row(s). Map columns to fields (* = required). PO will default to {orderNumber}.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {IMPORT_FIELDS.map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className={`text-xs w-40 shrink-0 ${f.required ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
                          {f.label}{f.required ? ' *' : ''}
                        </Label>
                        <select value={importMappings[f.key] || ''} onChange={e => setImportMappings(m => ({ ...m, [f.key]: e.target.value }))}
                          className="h-8 flex-1 rounded-lg border border-input bg-background px-2 text-xs">
                          <option value="">— Not mapped —</option>
                          {importParsed.columns.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {importMappings[f.key] && <Check className="h-4 w-4 text-green-600 shrink-0" />}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-start gap-2">
                    <Button onClick={() => setImportStep('preview')} disabled={!importMappings.supplier_invoice_number}>Preview Import</Button>
                    <Button variant="outline" onClick={() => setImportStep('upload')}>Back</Button>
                  </div>
                </div>
              )}

              {importStep === 'preview' && importParsed && (
                <div>
                  <p className="text-xs text-muted-foreground mb-3">Preview (first 10 rows):</p>
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-[11px] border-collapse">
                      <thead><tr className="bg-muted/50 border-b">
                        <th className="px-2 py-1.5 text-left">Invoice #</th>
                        <th className="px-2 py-1.5 text-left">Supplier</th>
                        <th className="px-2 py-1.5 text-left">Date</th>
                        <th className="px-2 py-1.5 text-left">Description</th>
                        <th className="px-2 py-1.5 text-right">Qty</th>
                        <th className="px-2 py-1.5 text-right">Price</th>
                        <th className="px-2 py-1.5 text-right">Net</th>
                      </tr></thead>
                      <tbody>
                        {importParsed.preview.slice(0, 10).map((row, i) => {
                          const g = (field) => { const col = importMappings[field]; return col ? (row[col] ?? '') : ''; };
                          return (
                            <tr key={i} className="border-b">
                              <td className="px-2 py-1 font-semibold">{g('supplier_invoice_number')}</td>
                              <td className="px-2 py-1">{g('supplier_name') || g('supplier_code')}</td>
                              <td className="px-2 py-1">{g('invoice_date')}</td>
                              <td className="px-2 py-1 max-w-[150px] truncate">{g('item_description')}</td>
                              <td className="px-2 py-1 text-right">{g('quantity')}</td>
                              <td className="px-2 py-1 text-right">{g('unit_price')}</td>
                              <td className="px-2 py-1 text-right font-semibold">{g('net_price')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-start gap-2">
                    <Button onClick={runImport} disabled={importProcessing}>
                      {importProcessing ? 'Importing...' : `Import ${importParsed.row_count} Row(s)`}
                    </Button>
                    <Button variant="outline" onClick={() => setImportStep('map')}>Back</Button>
                  </div>
                </div>
              )}

              {importStep === 'result' && importResult && (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="border rounded-lg p-4 bg-green-50">
                      <p className="text-2xl font-bold text-green-700">{importResult.created_count}</p>
                      <p className="text-xs text-green-600">Invoice(s) Created</p>
                    </div>
                    <div className="border rounded-lg p-4 bg-red-50">
                      <p className="text-2xl font-bold text-red-700">{importResult.error_count}</p>
                      <p className="text-xs text-red-600">Error(s)</p>
                    </div>
                  </div>
                  {importResult.created?.length > 0 && (
                    <div className="mb-4 space-y-1">
                      {importResult.created.map(c => (
                        <div key={c.invoice_ref} className="px-3 py-2 rounded border bg-green-50 text-xs cursor-pointer hover:bg-green-100"
                          onClick={() => navigate(`/invoices/${c.invoice_ref}`)}>
                          <span className="font-mono font-semibold text-primary">{c.invoice_ref}</span> — {c.supplier_invoice_number} — {c.lines} line(s)
                        </div>
                      ))}
                    </div>
                  )}
                  {importResult.errors?.length > 0 && (
                    <div className="mb-4 space-y-1">
                      {importResult.errors.map((err, i) => (
                        <div key={i} className="px-3 py-2 rounded border border-red-200 bg-red-50 text-xs text-red-700">
                          <AlertTriangle className="h-3 w-3 inline mr-1" />{err}
                        </div>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" onClick={() => setShowImport(false)}>Close</Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
